/**
 * Put a food in the library by holding its barcode up to the camera.
 *
 * The library could only be added to by typing. That was the one way in that
 * always works and the slowest of the three, and it left the fast path — the
 * thing the app is actually for — reachable only from inside the add sheet,
 * where it is bound to a meal. So the only way to get a scanned product into
 * your foods was to eat something.
 *
 * The readers are the sheet's, unchanged: `ScanPane` runs the barcode loop
 * against the live feed and the shutter reads a label, and a photograph that
 * refuses falls through to the by-hand form as it does everywhere else.
 *
 * **Nothing is logged here, and that is the whole difference.** Logging needs a
 * meal to land in and this screen has none — the same reason no row on Foods
 * has a `+`. A barcode lookup persists the product on its way past
 * (`persist_draft` in `services/scan.py`), so by the time the answer arrives
 * the food exists; what is left is to check the numbers and decide whether to
 * keep it. So it lands on the editor for that food, which is the screen for
 * exactly those two questions.
 */

import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { ScanPane } from "../components/ScanPane";
import { closeCamera } from "../lib/camera";

export function FoodScan() {
  const navigate = useNavigate();
  const [frozen, setFrozen] = useState(false);
  /** Bumped to thaw a viewfinder stopped on a photograph; see `ScanPane`. */
  const [reset, setReset] = useState(0);

  /* Leaving the page is the end of scanning here, the way closing the sheet is
     in the add flow. `ScanPane` holds the camera across its own remounts on
     purpose — see `lib/camera.ts` — so somebody has to say when it is over, and
     on a route that somebody is the route. */
  useEffect(() => closeCamera, []);

  const found = useCallback(
    // The food exists by now, whichever reader answered. Its numbers are the
    // open question, so this goes where they can be corrected rather than
    // dropping the user back on a list to find it again.
    (food: { id: string }) => navigate(`/foods/${food.id}`),
    [navigate],
  );

  return (
    <div className="foodscan">
      <div className="editor__back">
        <button className="editor__back-link" onClick={() => navigate("/foods")}>
          ‹ Foods
        </button>
        {/* The way back out of a frozen frame, in the place the sheet puts it.
            Only once there is a still to thaw — before that it would be a
            control for undoing something nobody has done. */}
        {frozen && (
          <button className="sheet__action" onClick={() => setReset((n) => n + 1)}>
            Take another photo
          </button>
        )}
      </div>

      <h1 className="editor__title">Scan a food</h1>
      <p className="editor__meta">
        Hold up a barcode and it is added to your foods. Nothing is logged — this screen has no
        meal to log into.
      </p>

      <ScanPane resetToken={reset} onFrozen={setFrozen} onFound={found} />
    </div>
  );
}
